/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { QuestionBaseDirective } from '@domains/companies/register-form/models/component/question.base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { City } from '@wizbii/models';
import { CityWebservice, LanguageParams, QueryParamsType } from '@wizbii/webservices';
import { BehaviorSubject, fromEvent, Observable, of } from 'rxjs';
import { debounceTime, filter, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-form-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationPickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationPickerComponent
  extends QuestionBaseDirective
  implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy
{
  private static Counter = 0;

  private _disabled = false;
  private _value: City | null = null;
  private _focused = false;
  private _autofilled = false;

  private readonly _id = `wzb-location-picker-${++LocationPickerComponent.Counter}`;

  get elm_id(): string {
    return this.id ?? this._id;
  }

  /**
   * Indicates if the "Current Position" picker should be shown
   */
  @Input() hasCurrentPositionPicker!: boolean;

  /**
   * Location type for autocomplete research
   */
  @Input() locationType: QueryParamsType = 'city';
  @Input() language: LanguageParams = 'fr';

  /**
   * Filter result with countries array
   */
  @Input() countries?: string[] = ['FR'];

  @HostBinding('attr.aria-describedby') describedBy = '';

  @ViewChild('textfieldRef', { static: true }) textfieldRef!: ElementRef<HTMLInputElement>;

  control!: FormControl;

  cities$: Observable<City[]> = of([]);
  loading$ = new BehaviorSubject<boolean>(false);
  parentFormSubmitted$ = new BehaviorSubject<boolean>(false);

  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  set value(value: City | null) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.control.setValue(value);
    this.onChange(value);
    this.onTouched();
  }
  get value(): City | null {
    return this._value;
  }

  set focused(focused: boolean) {
    if (focused === this._focused) {
      return;
    }

    if (!focused) {
      this.onTouched();
    }

    this._focused = focused;
  }
  get focused(): boolean {
    return this._focused;
  }

  set autofilled(autofilled: boolean) {
    this._autofilled = coerceBooleanProperty(autofilled);
  }
  get autofilled(): boolean {
    return this._autofilled;
  }

  get title(): string | undefined {
    return this.question?.title ?? '';
  }

  get subtitle(): string | undefined {
    return this.question?.subtitle ?? '';
  }

  constructor(
    private readonly cityWebservice: CityWebservice,
    private readonly cdr: ChangeDetectorRef,
    private readonly controlContainer: ControlContainer
  ) {
    super();
  }

  onChange: (city: City | null) => void = (_: City | null) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  ngOnInit(): void {
    this.control = this.initFormControl(this.question);

    if (this.controlContainer?.formDirective) {
      (this.controlContainer.formDirective as FormGroupDirective).ngSubmit.pipe(untilDestroyed(this)).subscribe({
        next: (_) => {
          this.parentFormSubmitted$.next(true);
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.cities$ = fromEvent(this.textfieldRef?.nativeElement, 'input').pipe(
      map((event: any) => event?.target['value'] as string),
      map((query) => query.trim().replace(/[\s]{2,}/g, ' ')),
      debounceTime(300),
      tap((query) => {
        this.value = query ? new City({ city: query }) : null;
        this.loading$.next(true);
      }),
      switchMap((query) =>
        query.length >= 1
          ? this.cityWebservice.getBy({
              query,
              countries: this.countries,
              type: this.locationType,
              language: this.language,
            })
          : of([])
      ),
      tap((cities) => {
        this.loading$.next(false);
        if (!this.focused && cities[0]) {
          this.value = new City({ ...cities[0], address: null });
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  doClosed(): void {
    if (!this.control?.errors?.cityRequired && !this.control?.errors?.required) {
      return;
    }

    this.cities$
      .pipe(
        first(),
        map((cities) => cities[0]),
        filter((city) => !!city)
      )
      .subscribe({
        next: (city) => {
          this.value = new City({ ...city, address: null });
          this.focused = false;
        },
      });
  }

  // Control Value Accessor
  setDisabledState = (isDisabled: boolean): void => {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  };
  registerOnChange = (fn: (city: City | null) => void): void => {
    this.onChange = (city: City | null) => {
      fn(city);
      this.cdr.markForCheck();
    };
  };
  registerOnTouched = (fn: () => void): void => {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  };
  writeValue = (value: City): City => (this.value = value ? new City({ ...value, address: null }) : new City());

  displayFn = (city: City): string => (city ? city.short : '');
  selectOption = (value: City): City => (this.value = new City({ ...value, address: null }));
  updateAutofilled = (event: any): void => (this.autofilled = event.isAutofilled);
  trackByCityPlaceId = (_: any, city: City): string => city.cityPlaceId;

  next(control: FormControl): void {
    this.control.markAllAsTouched();
    this.control.updateValueAndValidity();
    this.nextQuestion.emit(control);
  }

  ngOnDestroy(): void {
    this.parentFormSubmitted$.complete();
    this.loading$.complete();
  }
}

/**
 * Module exporting the LocationPicker and their dependencies
 */
@NgModule({
  imports: [CommonModule, MatAutocompleteModule, MatInputModule, MatFormFieldModule, ReactiveFormsModule],
  declarations: [LocationPickerComponent],
  exports: [LocationPickerComponent],
})
export class LocationPickerModule {}
